import keyBy from 'lodash/keyBy';

import { setIn } from '@lyearn/core/utils/immutable';
import { adaptEnumValue, adaptFromEnumValue } from '@/modules/reporting/utils/adapt';
import { MeasureAggregatorEnum, TagAvailableOnEnum } from '@/types/schema';

import { ReportTagUnionFragment } from '../../graphql/query/generated/reportTags';
import { ReportMetadataFilter, ReportMetadataResponse, ReportMetadataType } from './types';

interface Params {
  isOKREnabled?: boolean;
  response: ReportMetadataResponse;
  tags: ReportTagUnionFragment[];
}

const isDateOrMonthFilter = (filter: ReportMetadataFilter): boolean => {
  const hiddenFilterUniqueIds = ['date', 'month'];
  return hiddenFilterUniqueIds.includes(filter.uniqueId);
};

const getTagUniqueId = (id: string, availableOn: TagAvailableOnEnum) => {
  switch (availableOn) {
    case TagAvailableOnEnum.Event: {
      return `session_tag::${id}`;
    }
    default: {
      return `${adaptFromEnumValue(availableOn)}_tag::${id}`;
    }
  }
};

const getReportTagApplicableOn = (tagAvailableOn: TagAvailableOnEnum) => {
  switch (tagAvailableOn) {
    case TagAvailableOnEnum.User:
      return 'learnerTag';
    case TagAvailableOnEnum.Event:
      return 'sessionTag';
    default:
      return `${tagAvailableOn.toLowerCase()}Tag`;
  }
};

export const getComputedReportMetadata = (params: Params): ReportMetadataType => {
  const { isOKREnabled, response, tags } = params;
  let { filters: initialFilters, metrics: initialMetrics, views, tags: responseTags } = response;

  const tagViews = tags.reduce((acc, tag) => {
    const tagViewValues = tag.availableOn.map((availableOn) => ({
      uniqueId: getTagUniqueId(tag.id, availableOn),
      label: tag.name,
      metadata: {
        extraFields: [],
        applicableOn: getReportTagApplicableOn(availableOn),
        entities: [],
      },
      isExportable: true,
    }));

    acc.push(...tagViewValues);

    return acc;
  }, [] as any[]);

  const tagFilters = tags.reduce((acc, tag) => {
    const tagFilterValues = tag.availableOn.map((availableOn) => ({
      uniqueId: getTagUniqueId(tag.id, availableOn),
      label: tag.name,
      metadata: {
        tag,
        applicableOn: getReportTagApplicableOn(availableOn),
      },
      isExportable: true,
    }));

    acc.push(...tagFilterValues);

    return acc;
  }, [] as any[]);

  views = [...(views ?? []), ...tagViews];
  initialFilters = [...(initialFilters ?? []), ...tagFilters];

  if (!isOKREnabled) {
    views = views.filter((view) => view.metadata.applicableOn !== 'okr');
    initialMetrics = initialMetrics.filter((metric) => metric.applicableOn !== 'okr');
    initialFilters = initialFilters.filter((filter) => filter.metadata.applicableOn !== 'okr');
  }

  const propertyByUniqueId = views.reduce<ReportMetadataType['propertyByUniqueId']>(
    (accumulator, view) => {
      view.metadata.properties?.forEach((property) => {
        accumulator[property.uniqueId] = property;
      });
      return accumulator;
    },
    {},
  );

  const filters = initialFilters
    .filter((filter) => !isDateOrMonthFilter(filter))
    .map((filter) => {
      if (filter.metadata.tag) {
        return setIn(filter, 'metadata.tag', filter.metadata.tag);
      }
      return filter;
    });

  const metrics = initialMetrics.map(({ applicableAggregators, ...rest }) => ({
    ...rest,
    applicableAggregators: applicableAggregators.map(adaptEnumValue) as MeasureAggregatorEnum[],
  }));

  return {
    filters,
    metrics: metrics.filter((metric) => !metric.hiddenFromCustomReports),
    views: views.filter((view) => !view.hiddenFromCustomReports),
    tags: responseTags,
    filterByUniqueId: keyBy(filters, 'uniqueId'),
    metricByUniqueId: keyBy(metrics, 'uniqueId'),
    viewByUniqueId: keyBy(views, 'uniqueId'),
    propertyByUniqueId,
    tagByUniqueId: keyBy(responseTags, 'uniqueId'),
  };
};
